// Student Home dashboard (FR-P2) → docs/mockups/student-dashboard.html. KPI cards, a Needs-Attention
// nudge that deep-links into My Quality Checks, and My Preprints by stage.
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { StudentDashboard } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { Spinner } from "../../components/ui.js";
import { KpiCard, PitchGroup, PreprintColumn } from "./dashboard-ui.js";

/** Deep-link into My Quality Checks focused on Fixable rows that need the student fix loop. */
const QC_ATTENTION_HREF = "/quality-check?attention=1";

export function StudentDashboardView(): JSX.Element {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: qk.dashboardStudent,
    queryFn: () => api.get<StudentDashboard>("/dashboard/student"),
  });
  const firstName = user?.name?.split(" ")[0] || "there";

  if (isLoading || !data) return <Spinner label="Loading dashboard…" />;
  const k = data.kpis;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold">Good day, {firstName}! 👋</h1>
        <p className="mt-0.5 text-sm text-slate-500">Here's where your work stands.</p>
      </header>

      {/* KPI row */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Assignment slots" value={`${k.slotsUsed} / ${k.slotsCap}`} tone="blue" sublabel={`${Math.max(0, k.slotsCap - k.slotsUsed)} open to claim`} />
        <KpiCard label="Greenlits to date" value={k.greenlitsToDate} tone="green" sublabel="Reviewers invited" />
        <KpiCard label="Proposals" value={k.proposals} tone="violet" sublabel={`${k.greenlitRate}% greenlit rate`} />
        <KpiCard label="Needs attention" value={k.needsAttention} tone="red" sublabel="Fixable reviewers" />
      </div>

      {/* Needs-attention nudge → My Quality Checks focused on the Fixable QC to edit */}
      {k.needsAttention > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-800">
            ⚠️ <strong>{k.needsAttention} reviewer{k.needsAttention === 1 ? "" : "s"} need a fix.</strong> An AE left comments on
            proposals you submitted — edit &amp; mark Fixed.
          </p>
          <Link to={QC_ATTENTION_HREF} className="text-sm font-medium text-amber-800 hover:underline">
            Go to My Quality Checks →
          </Link>
        </div>
      )}

      {/* My Preprints — R-002: Active for me precedes In Pipeline; R-003: Team + Editorial
          pitches share the To-Pitch group (blue Team / amber Editorial). */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">📄 My Preprints</h2>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <PreprintColumn title="Claimed" cards={data.preprints.claimed} tone="slate" emptyLabel="No claimed preprints." />
          <PitchGroup team={data.preprints.toPitchTeam} editorial={data.preprints.toPitchEditorial} />
          <PreprintColumn title="Active for me" cards={data.preprints.activeForMe} tone="green" emptyLabel="No active preprints for you." />
          <PreprintColumn title="In Pipeline" cards={data.preprints.inPipeline} tone="amber" emptyLabel="Nothing in the editorial pipeline." />
        </div>
      </section>
    </div>
  );
}
